import React, { useState } from 'react';
import cc from 'classcat';
import { Helmet } from 'react-helmet-async';
import { useEffectOnce } from 'usehooks-ts';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import UserBox from 'components/UserBox';
import * as Router from 'modules/Router';
import Auth from 'modules/Auth';
import User from 'modules/User';
import { Typography, TypographyVariant } from 'components/Typography';
import Heading from 'components/Heading';
import AccessDenied from 'components/AccessDenied';
import Avatar from 'components/Avatar';
import type { LoginAdapterProps, LoginProps } from './types';
import LoginForm from './LoginForm';
import { LoginFooter } from './components/LoginFooter';
import { meta } from './data';
import './loginModal.css';

const getMessage = ({ search }: { search: string }) =>
  Object.fromEntries(new URLSearchParams(search).entries()).message;

const Login = ({ location: { search }, returningPlayer, loginError }: LoginProps) => {
  const [message] = useState(getMessage({ search }));
  const showWelcomeBack = Boolean(returningPlayer);

  if (loginError) {
    return (
      <AccessDenied
        id="loginBox"
        className="userBox--white loginBox"
        route="login"
        prompt={loginError?.prompt}
        email={loginError?.email}
        emailMasked={loginError?.emailMasked}
        overScroll
      />
    );
  }

  return (
    <UserBox
      id="loginBox"
      className={cc([
        'userBox--white loginBox',
        {
          'loginBox--welcomeBack': showWelcomeBack
        }
      ])}
      overScroll
      footer={<LoginFooter search={search} showWelcomeBack={showWelcomeBack} />}
    >
      <div className="loginBox__headingContainer">
        {returningPlayer ? (
          <>
            <Avatar className="loginBox__avatar" image={returningPlayer.avatar} />
            <Typography variant={TypographyVariant.HeaderLg}>
              <Heading
                level={3}
                className="loginBox__heading"
              >{`Welcome back, ${returningPlayer.firstName}!`}</Heading>
            </Typography>
            <Typography variant={TypographyVariant.BodyMdStrong}>
              <Heading level={4} className="loginBox__subHeading">
                {message || 'Log in to MrQ'}
              </Heading>
            </Typography>
          </>
        ) : (
          // TODO: Doesn't use Heading component
          <h3 className="loginBox__heading">{message || 'Log in to MrQ'}</h3>
        )}
      </div>
      {/* The key is declared to reset the login when switching between welcome back login */}
      {/* and regular login which resets the password field */}
      <LoginForm key={showWelcomeBack ? 'welcomeBackLogin' : 'regularLogin'} />
    </UserBox>
  );
};

const LoginAdapter = ({ location }: LoginAdapterProps) => {
  const returningPlayer = useAppSelector(User.selectors.getReturningPlayer);
  const loginError = useAppSelector(Auth.selectors.getLoginError);
  const dispatch = useAppDispatch();

  useEffectOnce(() => {
    dispatch(User.actions.setReturningPlayerFromCookie());
    if (getMessage(location)) {
      dispatch(Router.actions.stripSearch(location));
    }
  });

  return (
    <>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
      </Helmet>
      <Login loginError={loginError} location={location} returningPlayer={returningPlayer} />
    </>
  );
};

export default LoginAdapter;
